import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import '../../styles/marketplace.css';
import './MyReportsPage.css';

const MyReportsPage = () => {
  const [labReports, setLabReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // Initialize theme
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme') || 'light';
    const darkMode = savedTheme === 'dark';
    setIsDarkMode(darkMode);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !isDarkMode;
    setIsDarkMode(newDarkMode);
    const theme = newDarkMode ? 'dark' : 'light';
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  };

  useEffect(() => {
    fetchLabReports();
  }, []);

  const fetchLabReports = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await api.get('/lab-tests/patient/reports');
      
      if (response.data.success) {
        setLabReports(response.data.data);
      } else {
        setError('Failed to fetch lab reports');
      }
    } catch (err) {
      console.error('Error fetching lab reports:', err);
      setError('Error loading lab reports. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <div className="my-reports-page min-h-screen">
      {/* Dark Mode Toggle Button */}
      <button
        onClick={toggleDarkMode}
        className="dark-mode-toggle-btn"
        title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
      >
        {isDarkMode ? (
          <svg className="w-5 h-5 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
          </svg>
        ) : (
          <svg className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
          </svg>
        )}
      </button>

      {/* Header */}
      <div className="reports-header">
        <div className="header-pattern"></div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative z-10">
          <div className="text-center">
            <button
              onClick={() => navigate('/lab-tests')}
              className="back-button"
            >
              ←
            </button>
            
            <h1 className="reports-title">
              📄 My Lab Reports
            </h1>
            <p className="reports-subtitle">
              Access and download your completed lab test reports
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <div className="error-card">
            <div className="error-text">{error}</div>
          </div>
        )}

        {loading ? (
          <div className="loading-card">
            <div className="loading-icon">⏳</div>
            <div className="loading-text">Loading your lab reports...</div>
          </div>
        ) : labReports.length === 0 ? (
          <div className="empty-state-card">
            <div className="empty-icon">📄</div>
            <h2 className="empty-title">
              No Lab Reports Available
            </h2>
            <p className="empty-description">
              There are no lab reports available for you right now. Your reports will appear here once they are completed and ready.
            </p>
            <div className="empty-actions">
              <button
                onClick={() => navigate('/lab-tests/my-tests')}
                className="btn-primary"
              >
                View My Lab Tests
              </button>
              <button
                onClick={() => navigate('/lab-tests')}
                className="btn-secondary"
              >
                Browse Lab Tests
              </button>
            </div>
          </div>
        ) : (
          <div className="reports-container">
            <div className="reports-header-section">
              <h2 className="reports-section-title">
                Lab Report History
              </h2>
              <div className="reports-count-badge">
                {labReports.length} {labReports.length === 1 ? 'Report' : 'Reports'}
              </div>
            </div>

            <div className="reports-grid">
              {labReports.map((report) => (
                <div
                  key={report.reportId}
                  className="report-card"
                >
                  <div className="report-card-content">
                    <div className="report-info">
                      <h3 className="report-test-name">
                        {report.testName}
                      </h3>
                      <div className="report-details-grid">
                        <div className="report-detail">
                          <span className="report-detail-label">Referred by: </span>
                          <span className="report-detail-value">{report.doctorName}</span>
                        </div>
                        <div className="report-detail">
                          <span className="report-detail-label">Order Date: </span>
                          <span className="report-detail-value">{formatDate(report.orderDate)}</span>
                        </div>
                        {report.testDate && (
                          <div className="report-detail">
                            <span className="report-detail-label">Test Date: </span>
                            <span className="report-detail-value">{formatDate(report.testDate)}</span>
                          </div>
                        )}
                        {report.reportGeneratedDate && (
                          <div className="report-detail">
                            <span className="report-detail-label">Report Date: </span>
                            <span className="report-detail-value">{formatDate(report.reportGeneratedDate)}</span>
                          </div>
                        )}
                        <div className="report-detail">
                          <span className="report-detail-label">Price: </span>
                          <span className="report-detail-value-price">${report.price}</span>
                        </div>
                      </div>
                    </div>

                    <div className="report-actions">
                      {report.reportStatus === 'ready' && report.reportUrl ? (
                        <div className="report-buttons">
                          <button
                            onClick={() => window.open(report.reportUrl, '_blank')}
                            className="btn-view-report"
                          >
                            👁️ View Report
                          </button>
                          <button
                            onClick={() => {
                              const link = document.createElement('a');
                              link.href = report.reportUrl;
                              link.download = `${report.testName}_Report.pdf`;
                              link.click();
                            }}
                            className="btn-download-report"
                          >
                            📥 Download PDF
                          </button>
                        </div>
                      ) : (
                        <div className="report-pending">
                          <div className="pending-icon">⏳</div>
                          Lab report is yet to come.<br />
                          Please check again later.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MyReportsPage;
